<script setup>
import { computed } from 'vue';
import { unwrapPdfLineBreaks } from '@/utils/stringUtils';

const props = defineProps({
  body: String,
  disabled: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: "Digite aqui o conteúdo..."
  },
  maxlength: {
    type: Number,
    default: null
  },
  showCounter: {
    type: Boolean,
    default: false
  },
  // Quando true, texto colado passa por unwrapPdfLineBreaks (remove quebras
  // de linha artificiais de textos extraídos de PDF) antes de ser inserido
  unwrapPastedLineBreaks: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:body']);

function handlePaste(event) {
  if (!props.unwrapPastedLineBreaks) return;

  const pasted = event.clipboardData?.getData('text/plain');
  if (!pasted || !/[\r\n]/.test(pasted)) return;

  event.preventDefault();
  const normalized = unwrapPdfLineBreaks(pasted);
  const el = event.target;
  el.setRangeText(normalized, el.selectionStart, el.selectionEnd, 'end');
  emit('update:body', el.value);
}

const model = computed({
  get: () => props.body,
  set: (value) => emit('update:body', value)
});

const currentLength = computed(() => (props.body || '').length);

const isNearLimit = computed(() => {
  if (!props.maxlength) return false;
  return currentLength.value >= props.maxlength * 0.9;
});

const isAtLimit = computed(() => {
  if (!props.maxlength) return false;
  return currentLength.value >= props.maxlength;
});
</script>

<template>
  <div class="custom-textarea-wrapper">
    <textarea
      v-model="model"
      class="custom-textarea"
      :placeholder="props.placeholder"
      :disabled="props.disabled"
      :maxlength="props.maxlength || undefined"
      @paste="handlePaste"
    ></textarea>

    <div
      v-if="props.showCounter && props.maxlength"
      class="custom-textarea-counter"
      :class="{
 'custom-textarea-counter--near': isNearLimit && !isAtLimit,
        'custom-textarea-counter--at': isAtLimit
      }"
    >
      {{ currentLength }} / {{ props.maxlength }}
    </div>
  </div>
</template>

<style scoped>
.custom-textarea-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
}

.custom-textarea {
  width: 100%;
  min-height: 200px;
  padding: 0.625rem 0.75rem;
  border: 1px solid var(--border-color);
  background-color: var(--surface);
  color: var(--text-dark);
  font-size: 1.1rem;
  line-height: 1.5;
  border-radius: 8px;
  transition: border-color 0.2s, box-shadow 0.2s;
  resize: vertical;
}

.custom-textarea:focus {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}

.custom-textarea:disabled {
  background-color: var(--surface-2);
  cursor: not-allowed;
  opacity: 0.7;
}

.custom-textarea-counter {
  align-self: flex-end;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: var(--text-subtle);
  font-variant-numeric: tabular-nums;
  transition: color 0.15s;
}

.custom-textarea-counter--near {
  color: var(--warning);
}

.custom-textarea-counter--at {
  color: var(--danger-hover);
  font-weight: 600;
}
</style>